import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { DollarSign, ShoppingCart, CheckCircle, XCircle, Download, Calendar } from 'lucide-react';

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, expired
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchAnalytics();
  }, [user, navigate]);

  const fetchAnalytics = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/admin/all-purchases`,
        { withCredentials: true }
      );
      setData(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      alert('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!data || !data.purchases) return;

    const headers = ['Purchase ID', 'User Name', 'User Email', 'User Phone', 'Paper Title', 'Amount', 'Status', 'Purchased Date', 'Days Remaining', 'Payment ID'];
    const rows = data.purchases.map(p => [
      p.purchase_id,
      p.user_name,
      p.user_email,
      p.user_phone || 'N/A',
      p.paper_title,
      p.amount,
      p.status,
      new Date(p.purchased_at).toLocaleDateString(),
      p.is_active ? p.days_remaining : 'Expired',
      p.razorpay_payment_id
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `csed-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const getFilteredPurchases = () => {
    if (!data) return [];
    if (filter === 'active') return data.purchases.filter(p => p.is_active);
    if (filter === 'expired') return data.purchases.filter(p => !p.is_active && p.status === 'paid');
    return data.purchases;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E87722] mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading analytics...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const filteredPurchases = getFilteredPurchases();

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-[#1E3A5F] mb-2">Purchase Analytics</h1>
            <p className="text-gray-600">Track revenue, purchases, and user activity</p>
          </div>
          <Button
            onClick={downloadCSV}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 bg-gradient-to-br from-[#E87722] to-[#d66a1a] text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90 mb-1">Total Revenue</p>
                <p className="text-3xl font-bold">₹{data?.analytics?.total_revenue || 0}</p>
              </div>
              <DollarSign className="h-12 w-12 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-[#1E3A5F] to-[#2a4a73] text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90 mb-1">Total Purchases</p>
                <p className="text-3xl font-bold">{data?.analytics?.total_purchases || 0}</p>
              </div>
              <ShoppingCart className="h-12 w-12 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-600 to-green-700 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90 mb-1">Active Access</p>
                <p className="text-3xl font-bold">{data?.analytics?.active_purchases || 0}</p>
              </div>
              <CheckCircle className="h-12 w-12 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-red-600 to-red-700 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90 mb-1">Expired</p>
                <p className="text-3xl font-bold">{data?.analytics?.expired_purchases || 0}</p>
              </div>
              <XCircle className="h-12 w-12 opacity-50" />
            </div>
          </Card>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-2">
          <Button
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'bg-[#E87722] text-white' : 'bg-white text-gray-700 border'}
          >
            All ({data?.purchases?.length || 0})
          </Button>
          <Button
            onClick={() => setFilter('active')}
            className={filter === 'active' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 border'}
          >
            Active ({data?.analytics?.active_purchases || 0})
          </Button>
          <Button
            onClick={() => setFilter('expired')}
            className={filter === 'expired' ? 'bg-red-600 text-white' : 'bg-white text-gray-700 border'}
          >
            Expired ({data?.analytics?.expired_purchases || 0})
          </Button>
        </div>

        {/* Purchases Table */}
        <Card className="bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paper
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Access
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPurchases.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      No purchases found
                    </td>
                  </tr>
                ) : (
                  filteredPurchases.map((purchase) => (
                    <tr key={purchase.purchase_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{purchase.user_name}</div>
                        <div className="text-sm text-gray-500">{purchase.user_email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-700">{purchase.user_phone || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{purchase.paper_title}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-[#E87722]">₹{purchase.amount}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(purchase.purchased_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          purchase.status === 'paid' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {purchase.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {purchase.is_active ? (
                          <span className="flex items-center text-sm text-green-600">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            {purchase.days_remaining} days
                          </span>
                        ) : (
                          <span className="flex items-center text-sm text-red-600">
                            <XCircle className="h-4 w-4 mr-1" />
                            Expired
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Footer />
    </div>
  );
}
